/**
 * App-level right-click menu for capturable cells.
 *
 * A single instance (mounted once under KoryProvider), not one menu per cell —
 * any `[data-kory-cell]` right-click calls `openCellMenu(x, y, ctx)` on the
 * provider, which positions this menu at the cursor via a fixed 0-size anchor.
 * "Explain" sends a cell-context turn immediately (Grokbot-style summon).
 * "Ask Kory about this value" captures the cell and opens the launcher ready
 * to ask something else.
 */
import { ArrowDownRight, ArrowUpRight, SearchCheck } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ACCENTS, ITEM_IDENTITY } from "@/components/l10/l10-theme";
import { useKory } from "@/contexts/kory-context";
import { RHYTHM_QUERY_KEY, type RhythmPayload } from "@/components/kory/rhythm-types";
import { explainStaysOnOverview } from "@/lib/kory-capture-pointer";
import { EXPLAIN_CELL_PROMPT } from "@/lib/kory-cell-context";

/** Kory mark as a plain <img> (green on light / cream on dark via class-based
 *  dark:) — KoryIcon would pull in useTheme. Shared by both menu items. */
function KoryMenuMark() {
  return (
    <>
      <img src="/kory-green.svg" alt="" width={14} height={14} className="mr-2 dark:hidden" />
      <img src="/kory-cream.svg" alt="" width={14} height={14} className="mr-2 hidden dark:inline" />
    </>
  );
}

export function KoryCellContextMenu() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    cellMenu,
    closeCellMenu,
    attachCell,
    clearCells,
    openLauncher,
    sendMessage,
    cellIssueHandler,
    openKpiInvestigation,
  } = useKory();
  const open = cellMenu != null;
  // Canonical issue icon + accent — the composer type switcher uses the same
  // ones, pulled from the registry so they track the item identity, and the
  // amber accent makes the Lightbulb read as "issue". Resolved at render (not
  // module scope) so merely importing this file never evaluates ``ACCENTS`` —
  // that keeps partial ``l10-theme`` test mocks (ITEM_IDENTITY only) working.
  const IssueIcon = ITEM_IDENTITY.issue.icon;
  const issueIconAccent = ACCENTS[ITEM_IDENTITY.issue.accent].fg;

  return (
    <DropdownMenu
      // Non-modal: a modal menu locks `body { pointer-events: none }` to trap
      // interaction. Navigating away on select interrupts Radix's close cleanup
      // before it restores that — which left the whole Kory page unclickable.
      modal={false}
      open={open}
      onOpenChange={(next) => {
        if (!next) closeCellMenu();
      }}
    >
      {/* A 0-size anchor pinned at the cursor so the menu opens where the user
          right-clicked. Always mounted; only the position changes. */}
      <DropdownMenuTrigger asChild>
        <span
          aria-hidden
          style={{
            position: "fixed",
            left: cellMenu?.x ?? 0,
            top: cellMenu?.y ?? 0,
            width: 0,
            height: 0,
          }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={2}
        className="w-60"
        // Don't restore focus to the (invisible) anchor on close: both items
        // hand focus to another surface (the launcher popover / the IDS
        // composer), and the default focus-restore is what slammed the
        // just-opened popover shut as an interact-outside event.
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuItem
          onSelect={() => {
            const cell = cellMenu?.cell;
            if (!cell) return;
            // Explain is a singular turn about THIS cell — drop any cells the
            // composer already had staged so they don't ride along.
            clearCells();
            attachCell(cell);
            closeCellMenu();
            // Close first, then send on the NEXT frame — same Radix close-cleanup
            // race as the launcher path below. Stay on Overview only when Rhythm
            // has CoS on (same cache Overview already loaded) so a non-CoS
            // Overview still opens /kory, and a CoS Overview stays even while
            // the agent section is in Suspense.
            const cosEnabled = queryClient.getQueryData<RhythmPayload>(RHYTHM_QUERY_KEY)?.cos_enabled;
            const stayOnOverview = explainStaysOnOverview(window.location.pathname, cosEnabled);
            requestAnimationFrame(() => {
              void sendMessage(EXPLAIN_CELL_PROMPT, { startFresh: true });
              if (!stayOnOverview) navigate("/kory");
            });
          }}
        >
          <KoryMenuMark />
          Explain
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            const cell = cellMenu?.cell;
            if (!cell) return;
            attachCell(cell);
            // No toast: Kory opens in place (top-right), and a top-right sonner
            // toast would both be redundant ("opening" — it's right there) and
            // collide with the popover — its portal mounting in the same corner
            // trips the popover's dismissable layer and slams the tray shut.
            // Stay on the page: open the header launcher in place (preset-only
            // "ask about this value" mode) instead of routing to /kory. Close
            // the menu first, then open on the NEXT frame — opening synchronously
            // races Radix's close cleanup (the dropdown's focus-restore reads as
            // an interact-outside on the just-opened popover).
            closeCellMenu();
            requestAnimationFrame(() => openLauncher());
          }}
        >
          <KoryMenuMark />
          Ask Kory about this value
        </DropdownMenuItem>
        {/* Start a KPI investigation — only for a registry-backed cell
            (metricSlug is the investigation's KPI key; slug-less record/summary
            cells can't key one). The submenu picks the direction; the category +
            detail come from the dialog it opens. */}
        {cellMenu?.cell?.metricSlug && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <SearchCheck className="mr-2 h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              Start KPI Investigation
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {(
                [
                  { direction: "high", label: "Number seems high", Icon: ArrowUpRight },
                  { direction: "low", label: "Number seems low", Icon: ArrowDownRight },
                ] as const
              ).map(({ direction, label, Icon }) => (
                <DropdownMenuItem
                  key={direction}
                  onSelect={() => {
                    const cell = cellMenu?.cell;
                    if (!cell) return;
                    // Close first, then open on the NEXT frame — same Radix
                    // close-cleanup race as "Ask Kory about this value" above.
                    closeCellMenu();
                    requestAnimationFrame(() => openKpiInvestigation(cell, direction));
                  }}
                >
                  <Icon className="mr-2 h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        {/* Only inside the L10 runner (where ScorecardSection registers the
            handler) and only for a registry-backed cell (metricSlug present, so
            the issue can link back to the KPI). Hidden everywhere else —
            standalone scorecards, sales grids, drill-through record cells. */}
        {cellIssueHandler && cellMenu?.cell?.metricSlug && (
          <DropdownMenuItem
            onSelect={() => {
              const cell = cellMenu?.cell;
              if (!cell) return;
              cellIssueHandler(cell);
              closeCellMenu();
            }}
          >
            <IssueIcon className={`mr-2 h-3.5 w-3.5 ${issueIconAccent}`} aria-hidden />
            Create issue
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
